import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { emailAddress, password_input } from "utilities/formSchemas";
import PasswordChecker from "components/password-and-confirm-password/passwordcheckerversion2/PasswordCheckerVersion2";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { useEffect, useRef, useState } from "react";
import * as Constant from "utilities/Constant";
import "./registration.scss";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useDispatch } from "react-redux";
import { SetPasswordRequest, useGetDecryptEmailQuery, usePatchSetPasswordMutation } from "pages/registration/registrationService";
import { setEmailAddress, setUserId } from "features/authSlice";
import { ErrorType } from "utilities/error";
import { LoadingOverlay } from "components/ui/loading-spinners";
import Modal from "components/modal/Modal";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Consent } from "components/consent/Consent";
import { setFSLeadContextUserVars, FLOW_CONTEXT, AUTH_OUTCOME, trackFSAuthError, trackFSAuthStep } from "utilities/full-story";
import { setResendCodeDeviceId } from "features/mfaSlice";
import Footer from "components/footer/Footer";
import { RegistrationDesktopInformation } from "pages/registration/RegistrationInformationPanel";

export const formSchema = z.object({
  email: emailAddress,
  password_input,
});

export default function RegistrationCoBorrower() {
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    setValue,
  } = useForm({
    resolver: zodResolver(formSchema),
  });

  const [error, setError] = useState(0);
  const [searchParams] = useSearchParams();
  const encryptedUser = searchParams.get("t")!;
  const {
    data: decryptedUserDetails,
    isLoading,
    isFetching,
  } = useGetDecryptEmailQuery(encryptedUser, {
    skip: !encryptedUser,
  });

  const navigate = useNavigate();
  useEffect(() => {
    if (decryptedUserDetails) {
      const { ex, em, nhDealAppId } = decryptedUserDetails;
      setFSLeadContextUserVars({ nh_deal_application_id: nhDealAppId });
      if (ex === true) {
        navigate("/", { replace: true });
      } else {
        setIsBtnDisabled((prevState) => !prevState);
        setValue("email", em);
      }
    }
  }, [decryptedUserDetails]);

  const [hasPwdError, setHasPwdError] = useState(false);
  const [isBtnDisabled, setIsBtnDisabled] = useState<boolean>(false);
  let showModal = false;
  if (!isLoading && !isFetching && !decryptedUserDetails) {
    showModal = true;
  }

  const isDesktop = useDesktopChecker();

  const [sendRequest, { data, isSuccess, isLoading: isRegistrationLoading }] = usePatchSetPasswordMutation();
  const dispatch = useDispatch();
  const submittedEmailRef = useRef("");
  const onSubmit = (data: SetPasswordRequest) => {
    if (!hasErrors && !hasPwdError) {
      submittedEmailRef.current = data.email;
      sendRequest(data)
        .unwrap()
        .catch((error: ErrorType) => {
          trackFSAuthError(FLOW_CONTEXT.co_borrower_registration, error);
          if (error.status == 409) {
            navigate("/", { replace: true });
          } else {
            setError(500);
          }
        });
    }
  };
  useEffect(() => {
    if (isSuccess && data) {
      setError(0);
      trackFSAuthStep(FLOW_CONTEXT.co_borrower_registration, AUTH_OUTCOME.otp_required);
      dispatch(setEmailAddress(submittedEmailRef.current));
      dispatch(setUserId(data.userId));
      dispatch(setResendCodeDeviceId(data.deviceAuthId));
      navigate("/registrationAuth", { replace: true });
    }
  }, [isSuccess, data]);

  const ispasswordFieldModified = (dirtyFields.password_input as boolean) || false;
  const hasErrors = !!Object.keys(errors).length;
  if (isRegistrationLoading) return <LoadingOverlay text="Registering..." />;

  return (
    <>
      {(isLoading || isFetching) && <LoadingOverlay text="Loading user details..." />}
      <div className="registration">
        {showModal && (
          <Modal
            defaultBody
            defaultBodyLabel="Something went wrong!"
            defaultBodyLabelIcon={regular("triangle-exclamation")}
            defaultBodySubContent={[
              "Please open the email we sent you and click on the link so that we can try again.",
              "We apologize for the inconvenience.",
            ]}
            footer={<></>}
          />
        )}
        <div className="registration__container">
          <div className="registration__form">
            <h1 className="registration__header">
              {" "}
              {isDesktop ? `Create an account to continue your application` : `Create an account to continue`}
            </h1>
            {error == 500 && (
              <div className="registration__error">
                {isDesktop
                  ? Constant.RegistrationError
                  : Constant.RegistrationError.map((error) => {
                    return <p key={error}>{error}</p>;
                  })}
              </div>
            )}
            <Form
              onSubmit={handleSubmit((data) => {
                onSubmit({
                  email: data.email as string,
                  password: data.password_input as string,
                  nhDealApplicationId: decryptedUserDetails?.nhDealAppId as string,
                });
              })}
            >
              <div className="row--item">
                <FormInput
                  formHandle={register("email")}
                  inputProps={{
                    name: "email",
                    placeholder: "Enter Your Email Address",
                    label: "Email Address",
                    errorMessage: errors.email?.message as string,
                    isReadOnly: true,
                  }}
                />
              </div>
              <div className="row--item">
                <Controller
                  name="password_input"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <PasswordChecker
                      onChange={onChange}
                      checkErrors={ispasswordFieldModified}
                      createLabel="Create Password"
                      setHasPasswordError={setHasPwdError}
                      setBtnVisible={setIsBtnDisabled}
                      createPasswordPlaceholderName="Create password"
                    />
                  )}
                />
              </div>
              <Button title="Continue" className="registration__button" disabled={isBtnDisabled} />
            </Form>
            <Consent text={Constant.PRIVACY_AND_TERMSOFUSE_CONSENT} />
          </div>
          <RegistrationDesktopInformation isDesktop={isDesktop} />
        </div>
      </div>
      <div className="registration__footer">
        <Footer />
      </div>
    </>
  );
}
